import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import {
    ListTag,
    TagInput,
    Vendor,
    VENDOR_CATEGORIES,
    VENDOR_MODULES,
    VendorCategory,
    VendorModule,
    VendorPayload,
} from '../../../types/lists.types';
import { vendorsApi } from '../../../utils/lists-api';
import TagCombobox from './TagCombobox';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vendor: Vendor | null;
    tags: ListTag[];
    onSaved: () => void;
}

const EMPTY: VendorPayload = {
    legalName: '',
    tradingName: '',
    abnNumber: '',
    website: '',
    contactName: '',
    phone: '',
    email: '',
    category: null,
};

export const VendorFormDialog: React.FC<Props> = ({ open, onOpenChange, vendor, tags, onSaved }) => {
    const [form, setForm] = useState<VendorPayload>(EMPTY);
    const [tagValue, setTagValue] = useState<TagInput>([]);
    const [modules, setModules] = useState<VendorModule[]>([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!open) return;
        if (vendor) {
            setForm({
                legalName: vendor.legalName,
                tradingName: vendor.tradingName,
                abnNumber: vendor.abnNumber ?? '',
                website: vendor.website ?? '',
                contactName: vendor.contactName ?? '',
                phone: vendor.phone ?? '',
                email: vendor.email ?? '',
                category: vendor.category,
            });
            setTagValue((vendor.tags || []).map((t) => t.id));
            setModules(vendor.showInModules || []);
        } else {
            setForm(EMPTY);
            setTagValue([]);
            setModules([]);
        }
        setErrors({});
    }, [open, vendor]);

    const set = (key: keyof VendorPayload, value: any) => setForm((f) => ({ ...f, [key]: value }));

    const toggleModule = (m: VendorModule) =>
        setModules((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.legalName.trim()) next.legalName = 'Legal Name is required';
        if (!form.tradingName.trim()) next.tradingName = 'Trading Name is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);
            const payload = { ...form, tags: tagValue, showInModules: modules };
            if (vendor) {
                await vendorsApi.update(vendor.id, payload);
                toast.success('Vendor updated');
            } else {
                await vendorsApi.create(payload);
                toast.success('Vendor created');
            }
            onSaved();
            onOpenChange(false);
        } catch (err: any) {
            // api.ts already surfaces the error toast; nothing to add here.
            console.error('Failed to save vendor', err);
        } finally {
            setSaving(false);
        }
    };

    const field = (
        key: keyof VendorPayload,
        label: string,
        opts: { required?: boolean; type?: string; placeholder?: string } = {}
    ) => (
        <div className="space-y-2">
            <Label htmlFor={`vendor-${key}`}>
                {label} {opts.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
                id={`vendor-${key}`}
                type={opts.type || 'text'}
                placeholder={opts.placeholder}
                value={(form[key] as string) || ''}
                onChange={(e) => set(key, e.target.value)}
            />
            {errors[key] && <p className="text-xs text-destructive">{errors[key]}</p>}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium">
                        {vendor ? 'Edit Vendor' : 'Add Vendor'}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        A vendor appears in a module's selector only when that module is ticked below
                        and the vendor is Active.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="py-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {field('legalName', 'Legal Name', { required: true })}
                            {field('tradingName', 'Trading Name', { required: true })}
                            {field('abnNumber', 'ABN Number')}
                            {field('website', 'Website', { type: 'url', placeholder: 'https://' })}
                            {field('contactName', 'Contact Name')}
                            {field('phone', 'Phone', { type: 'tel' })}
                            {field('email', 'Email', { type: 'email' })}

                            <div className="space-y-2">
                                <Label htmlFor="vendor-category">Category</Label>
                                <Select
                                    value={form.category || undefined}
                                    onValueChange={(v) => set('category', v as VendorCategory)}
                                >
                                    <SelectTrigger id="vendor-category" className="w-full bg-white">
                                        <SelectValue placeholder="Select a category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VENDOR_CATEGORIES.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Tags</Label>
                            <TagCombobox
                                options={tags}
                                value={tagValue}
                                onChange={setTagValue}
                                placeholder="Select or create tags"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Show in Modules</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border border-border p-3">
                                {VENDOR_MODULES.map((m) => (
                                    <label
                                        key={m}
                                        htmlFor={`vendor-module-${m}`}
                                        className="flex items-center gap-2 text-sm cursor-pointer"
                                    >
                                        <Checkbox
                                            id={`vendor-module-${m}`}
                                            checked={modules.includes(m)}
                                            onCheckedChange={() => toggleModule(m)}
                                        />
                                        {m}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="gap-2">
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {vendor ? 'Save Changes' : 'Add Vendor'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default VendorFormDialog;
